// store/slices/informationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import InformationService from "services/InformationService";

// Initial state for information slice
const initialState = {
  loading: false,
  information: {
    en: null,
    ar: null,
  },
  analyticsData: null,
  metaCampaignStats: null,
  metaCampaignLoading: false,
  message: "",
  messageKey: "", // Add this for translation keys
  showMessage: false,
  error: null,
};

// Async thunk to fetch both English and Arabic information
export const getInformation = createAsyncThunk(
  "information/getInformation",
  async ({ informationId }, { rejectWithValue }) => {
    try {
      const [enResponse, arResponse] = await Promise.all([
        InformationService.getInformation(informationId, 1), // English
        InformationService.getInformation(informationId, 4), // Arabic
      ]);

      return {
        en: enResponse,
        ar: arResponse,
      };
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error fetching information",
        messageKey: "meta.fetch_error",
      });
    }
  },
);

// Async thunk to update information
export const updateInformation = createAsyncThunk(
  "information/updateInformation",
  async (params, { rejectWithValue }) => {
    try {
      const response = await InformationService.updateInformation(params);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error updating information",
        messageKey: "meta.update_error",
      });
    }
  },
);

// Async thunk to get analytics data
export const getAnalyticsData = createAsyncThunk(
  "information/getAnalyticsData",
  async (languageId, { rejectWithValue }) => {
    try {
      const response = await InformationService.getAnalyticsData(languageId);
      return response;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error fetching analytics data",
        messageKey: "meta.analytics_fetch_error",
      });
    }
  },
);

// Meta Campaign Stats thunk
export const getMetaCampaignStats = createAsyncThunk(
  "information/getMetaCampaignStats",
  async ({ adAccountId, dateRange = 30 }, { rejectWithValue }) => {
    try {
      if (!adAccountId) {
        throw new Error("Ad Account ID is required");
      }

      const response = await InformationService.getMetaCampaignStats(
        adAccountId,
        dateRange,
      );

      // Handle different response structures
      if (response?.data) {
        return response.data;
      } else if (response?.success && response?.data) {
        return response;
      } else {
        return response;
      }
    } catch (err) {
      console.error("Meta Campaign Stats Error:", err);
      return rejectWithValue({
        message:
          err.response?.data?.message ||
          err.message ||
          "Error fetching Meta campaign stats",
        messageKey: "meta.campaign_stats_error",
      });
    }
  },
);

// Refresh Meta campaign data
export const refreshMetaCampaignStats = createAsyncThunk(
  "information/refreshMetaCampaignStats",
  async ({ adAccountId, dateRange = 30 }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        getMetaCampaignStats({ adAccountId, dateRange }),
      );
      return result.payload;
    } catch (err) {
      return rejectWithValue({
        message: err.message || "Error refreshing Meta campaign stats",
        messageKey: "meta.refresh_error",
      });
    }
  },
);

const informationSlice = createSlice({
  name: "information",
  initialState,
  reducers: {
    setInformation: (state, { payload }) => {
      state.information = payload;
    },
    clearMessage: (state) => {
      state.message = "";
      state.messageKey = "";
      state.showMessage = false;
      state.error = null;
    },
    clearMetaCampaignStats: (state) => {
      state.metaCampaignStats = null;
      state.metaCampaignLoading = false;
    },
    setMetaCampaignLoading: (state, { payload }) => {
      state.metaCampaignLoading = payload;
    },
    setSuccessMessage: (state, action) => {
      state.message = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
      state.showMessage = true;
    },
    setErrorMessage: (state, action) => {
      state.error = action.payload.message || "";
      state.messageKey = action.payload.messageKey || "";
      state.showMessage = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Information reducers
      .addCase(getInformation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInformation.fulfilled, (state, { payload }) => {
        state.information = {
          en: payload.en,
          ar: payload.ar,
        };
        state.loading = false;
        state.error = null;
      })
      .addCase(getInformation.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "meta.fetch_error";
        state.showMessage = true;
        state.error = payload?.message || payload;
      })

      // Update information reducers
      .addCase(updateInformation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInformation.fulfilled, (state) => {
        state.loading = false;
        state.showMessage = true;
        state.message = "Information updated successfully";
        state.messageKey = "meta.update_success";
        state.error = null;
      })
      .addCase(updateInformation.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "meta.update_error";
        state.showMessage = true;
        state.error = payload?.message || payload;
      })

      // Analytics data reducers
      .addCase(getAnalyticsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsData.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.analyticsData = payload;
        state.error = null;
      })
      .addCase(getAnalyticsData.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "meta.analytics_fetch_error";
        state.showMessage = true;
        state.error = payload?.message || payload;
      })

      // Meta Campaign Stats reducers
      .addCase(getMetaCampaignStats.pending, (state) => {
        state.metaCampaignLoading = true;
        state.error = null;
      })
      .addCase(getMetaCampaignStats.fulfilled, (state, { payload }) => {
        state.metaCampaignLoading = false;
        state.metaCampaignStats = payload;
        state.message = "Campaign data loaded successfully";
        state.messageKey = "meta.data_loaded_success";
        state.showMessage = true;
        state.error = null;
      })
      .addCase(getMetaCampaignStats.rejected, (state, { payload }) => {
        state.metaCampaignLoading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "meta.campaign_stats_error";
        state.showMessage = true;
        state.error = payload?.message || payload;
        state.metaCampaignStats = null;
      })

      // Refresh Meta Campaign Stats reducers
      .addCase(refreshMetaCampaignStats.pending, (state) => {
        state.metaCampaignLoading = true;
        state.error = null;
      })
      .addCase(refreshMetaCampaignStats.fulfilled, (state, { payload }) => {
        state.metaCampaignLoading = false;
        state.metaCampaignStats = payload;
        state.message = "Meta campaign data refreshed successfully";
        state.messageKey = "meta.refresh_success";
        state.showMessage = true;
        state.error = null;
      })
      .addCase(refreshMetaCampaignStats.rejected, (state, { payload }) => {
        state.metaCampaignLoading = false;
        state.message = payload?.message || payload;
        state.messageKey = payload?.messageKey || "meta.refresh_error";
        state.showMessage = true;
        state.error = payload?.message || payload;
      });
  },
});

export const {
  setInformation,
  clearMessage,
  clearMetaCampaignStats,
  setMetaCampaignLoading,
  setSuccessMessage,
  setErrorMessage,
} = informationSlice.actions;

export default informationSlice.reducer;
